import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Sheet } from '@heroui-pro/react';
import type { Employee } from '@/api/models';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { RateText } from '../../salariesMeta';
import { EmployeeFormView } from './EmployeeFormView';
import { useEmployeeForm, type EmployeeSaved } from './useEmployeeForm';

export type EmployeeFormDrawerProps = {
    isOpen: boolean;
    /** The employee being edited; `null` = create. */
    employee: Employee | null;
    onClose: () => void;
    /** Today's rates, for the pension hint; `null` when none are in force. */
    rates: RateText | null;
    /**
     * A write landed. Fired **before** `onClose`, and only on a success —
     * `onClose` alone cannot mean "saved" because it also fires on Cancel, on
     * the backdrop, and after a delete.
     */
    onSaved?: EmployeeSaved;
};

/**
 * Right-side drawer hosting the add / edit employee form — sibling of the Chart
 * of Accounts CRUD drawer and the asset form drawer (same HeroUI Pro `Sheet`
 * chrome, vocabulary and focus handling). The register stays visible behind it.
 * The Sheet owns focus trap and restore; the hook owns all behaviour.
 *
 * Unlike the asset drawer, nothing here produces a journal entry: hiring, editing
 * and terminating are register facts, not transactions. The only thing that posts
 * on this screen is a payroll run.
 *
 * **It is mounted by more than one page.** The Control Center opens this same
 * drawer for its "Add employee" action rather than growing a second employee
 * form. Two rules the register already follows are load-bearing for any caller:
 *
 * - **Mount it only while open.** The hook fires an ungated
 *   `GET employees?status=ALL` for the duplicate-personal-id check, so a drawer
 *   left mounted closed is a request on every page load.
 * - **Give it a `key` that changes per opening.** Every field is seeded from
 *   `employee` once, at mount; without a fresh mount the second "New employee"
 *   inherits the first one's draft.
 */
export const EmployeeFormDrawer: React.FC<EmployeeFormDrawerProps> = ({
    isOpen,
    employee,
    onClose,
    rates,
    onSaved,
}) => {
    const { t } = useTranslation('pages');
    const form = useEmployeeForm({ employee, onClose, onSaved });
    const isEdit = employee !== null;

    return (
        <Sheet
            isOpen={isOpen}
            onOpenChange={(open) => !open && onClose()}
            placement="right"
        >
            <Sheet.Backdrop>
                <Sheet.Content className="w-full sm:max-w-[560px]">
                    <Sheet.Dialog
                        aria-label={t(
                            isEdit
                                ? 'salaries.form.editTitle'
                                : 'salaries.form.createTitle',
                        )}
                        {...getNodeTestIdProp('salaries-form-drawer')}
                    >
                        <Sheet.CloseTrigger />
                        <Sheet.Header>
                            <Sheet.Heading>
                                <span className="flex flex-col gap-1">
                                    <span className="text-lg font-semibold">
                                        {t(
                                            isEdit
                                                ? 'salaries.form.editTitle'
                                                : 'salaries.form.createTitle',
                                        )}
                                    </span>
                                    <span className="text-sm font-normal text-muted">
                                        {t(
                                            isEdit
                                                ? 'salaries.form.editSubtitle'
                                                : 'salaries.form.createSubtitle',
                                        )}
                                    </span>
                                </span>
                            </Sheet.Heading>
                        </Sheet.Header>

                        <Sheet.Body>
                            <EmployeeFormView
                                form={form}
                                onCancel={onClose}
                                rates={rates}
                            />
                        </Sheet.Body>
                    </Sheet.Dialog>
                </Sheet.Content>
            </Sheet.Backdrop>
        </Sheet>
    );
};
